import Icon from '@components/utils/icons/icon';
import { StyledProps, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useVideoPlayerContext } from '@lib/pimster-stories/contexts/VideoPlayerContext';
import { SanitizedStoryPage } from '@lib/pimster-stories/types';
import { FC } from 'react';

import Video from './Video';

type Props = {
    storyPage: SanitizedStoryPage;
};

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: relative;
`;

const ButtonCircle = styled.button<StyledProps<{ visible: boolean }>>`
    z-index: 901;
    cursor: pointer;
    pointer-events: auto;
    position: fixed;
    border-radius: 100%;
    top: 50%;
    right: 50%;
    transform: translate(50%, -50%);
    background-color: rgba(0, 0, 0, 0.4);
    align-items: center;
    justify-content: center;
    transition: all 1s ease-in;
    display: ${({ visible }) => (visible ? 'flex' : 'none')};
    height: ${({ theme, visible }) => (visible ? theme.spacing.xxl : '0')};
    width: ${({ theme, visible }) => (visible ? theme.spacing.xxl : '0')};
    font: ${({ theme }) => theme.fonts.subtitle4};
    &:active {
        background-color: rgba(0, 0, 0, 0.6);
        height: ${(props) => `calc(1.2 * ${props.theme.spacing.xxl})`};
        width: ${(props) => `calc(1.2 * ${props.theme.spacing.xxl})`};
        transition: all 0.2s ease-in;
    }
`;

const VideoPlayer: FC<Props> = ({ storyPage }) => {
    const theme = useTheme();
    const {
        vid,
        playVisible,
        replayVisible,
        isMuted,
        playVideo,
        restartVideo,
        onCanPlayThrough,
        onEnded,
    } = useVideoPlayerContext();

    return (
        <Container className='video_container'>
            <Video
                ref={vid}
                storyPage={storyPage}
                preload='metadata'
                autoPlay={false}
                playsInline={true}
                onCanPlayThrough={onCanPlayThrough}
                muted={isMuted}
                onEnded={onEnded}
                controls={false}
                id={storyPage.subtitle}
            />
            <ButtonCircle visible={replayVisible} onClick={restartVideo}>
                <Icon
                    size='medium'
                    name='replay'
                    color={theme.palette.common.white}
                />
            </ButtonCircle>
            <ButtonCircle visible={playVisible} onClick={() => playVideo()}>
                <Icon
                    size='medium'
                    name='play_arrow'
                    color={theme.palette.common.white}
                />
            </ButtonCircle>
        </Container>
    );
};

export default VideoPlayer;
